import { isAfter } from 'tio-common/utils/date/is-after';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Service from '@ember/service';
import ENV from 'tio-employee/config/environment';
import { getSubdomain, getPartnerLogoUrl, isLocalhost } from 'tio-common/utils/hostname';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type { CompanyResource } from 'tio-data/data/resources/company.type';
import type { CompanySettingResource } from 'tio-data/data/resources/company-setting.type';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type PartnerService from './partner';
import type SessionContextService from './session-context';
import type SessionService from './session';
import {
  getCurrentDemoClientLogoUrl,
  getCurrentDemoPreRegisterCompany,
} from 'tio-employee/utils/career-pathways-demo-data';
import type DemoModeService from './demo-mode';
import type { Store } from '@warp-drive/core';

// Re-export for backwards compatibility if needed elsewhere
export type { HostnameParts } from 'tio-common/utils/hostname';

// These describe the sparse, plain-object demo/pre-register payloads (not loaded
// WarpDrive records), so they derive from the bare `*Resource` field types
// rather than the legacy-mode record types (which brand `constructor`/`[Type]`).
export type PreRegisterSettings = Partial<CompanySettingResource>;
export type PreRegisterCompany = Partial<CompanyResource>;

export default class PreRegisterService extends Service {
  @service declare sessionContext: SessionContextService;
  @service declare session: SessionService;
  @service declare store: Store;
  @service declare partner: PartnerService;
  @service declare demoMode: DemoModeService;

  @tracked sparseCompany?: PreRegisterCompany;
  #loadRevision = 0;

  /** Clears public identity, including the echoed auth key, and invalidates outstanding requests during logout. */
  clear() {
    ++this.#loadRevision;
    this.sparseCompany = undefined;
    this.conditions = [];
    this.session.publicKey = undefined;
  }
  @tracked conditions: ConditionModel[] = [];

  /**
   * Gets the subdomain from the current hostname.
   * Uses shared hostname utility for consistency across services.
   */
  get subdomain() {
    return getSubdomain();
  }

  get settings() {
    // @ts-expect-error seems like a bug
    return this.sparseCompany?.settings;
  }

  get companyName() {
    return this.sparseCompany?.name;
  }

  get programTitle() {
    return this.sparseCompany?.programTitle;
  }

  get logo() {
    if (this.demoMode.isActive) {
      return getCurrentDemoClientLogoUrl();
    }

    return getPartnerLogoUrl(this.subdomain, ENV.tioEnvironment);
  }

  get termsOfUse() {
    return this.conditions.find((condition) => condition.classification === 'app_terms_of_use');
  }

  get privacyPolicy() {
    return this.conditions.find((condition) => condition.classification === 'app_privacy_policy');
  }

  get customTerms() {
    return this.conditions.filter((condition) => condition.classification === 'custom');
  }

  get isEmployeeSubdomain() {
    return this.subdomain === 'employee' || this.subdomain === '';
  }

  /**
   * Checks if the app is running on localhost.
   * Exposed as a service property to allow mocking in tests.
   */
  get isLocalhost() {
    return isLocalhost();
  }

  /**
   * Determines if the email lookup should be shown.
   * Combines isEmployeeSubdomain and isLocalhost checks.
   */
  get shouldShowEmailLookup() {
    return this.isEmployeeSubdomain && !this.isLocalhost;
  }

  get securitySettings() {
    return this.settings?.security;
  }

  get launchDate() {
    return this.sparseCompany?.launchDate ?? '';
  }

  get isLaunched() {
    return this?.launchDate ? isAfter(new Date(), this.launchDate) : true;
  }

  get daysUntilLaunch() {
    if (!this.launchDate || this.isLaunched) return 0;

    const targetDate = new Date(this.launchDate);
    const currentDate = new Date();
    const timeDifference = targetDate.getTime() - currentDate.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

    return daysDifference;
  }

  emailLookup = async (email: string) => {
    // Auth context supplies x-auth-key automatically when the session has a publicKey.
    const headers = buildTioAuthHeaders({ apiKey: ENV.apiKey, contentType: 'application/json' });

    const response = await fetch(`${ENV.apiHost}/preregister/validate-email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers,
    });
    return response.json();
  };

  /**
   * Reloads public company context. The current company stays applied until the new
   * response lands, and late responses cannot replace a newer host or load.
   */
  load = async () => {
    const revision = ++this.#loadRevision;
    const subdomain = this.subdomain || 'employee';
    const isCurrent = () =>
      revision === this.#loadRevision && subdomain === (this.subdomain || 'employee');
    if (this.demoMode.isActive) {
      this.sparseCompany = getCurrentDemoPreRegisterCompany();
      this.conditions = [];
      return;
    }

    const conditionsRequest = this.#fetchTermsAndConditions(subdomain).then(
      (conditions) => ({ conditions }),
      (error: unknown) => ({ error })
    );

    try {
      const response = await fetch(`${ENV.apiHost}/preregister/${subdomain}`);
      if (!isCurrent()) return;
      if (!response.ok) {
        // Non-2xx: drop any previously cached company/publicKey rather than
        // continuing to echo a stale `x-auth-key` on subsequent requests.
        console.warn(
          `Failed to load preregister data from ${ENV.apiHost}/preregister/${subdomain} (status ${response.status})`
        );
        this.sparseCompany = undefined;
        this.session.publicKey = undefined;
        this.conditions = [];
        return;
      }
      const payload = await response.json();
      if (!isCurrent()) return;
      this.sparseCompany = payload;
      // Stash on the session so adapters/authenticators/routes can echo it
      // back as `x-auth-key` without a parallel pre-register lookup.
      this.session.publicKey = payload?.publicKey ?? undefined;
    } catch (error) {
      if (!isCurrent()) return;
      console.warn(
        `Failed to load preregister data from ${ENV.apiHost}/preregister/${subdomain}`,
        error
      );
      this.sparseCompany = undefined;
      this.session.publicKey = undefined;
      this.conditions = [];
      return;
    }

    const result = await conditionsRequest;
    if (!isCurrent()) return;
    if ('error' in result) {
      console.warn(
        `Failed to load preregister conditions from ${ENV.apiHost}/preregister/${subdomain}/conditions`,
        result.error
      );
      this.conditions = [];
    } else {
      this.conditions = result.conditions;
    }
  };

  async #fetchTermsAndConditions(subdomain: string): Promise<ConditionModel[]> {
    const { content } = (await this.store.request({
      url: `${ENV.apiHost}/preregister/${subdomain}/conditions`,
      method: 'GET',
      op: 'query',
    })) as { content: { data: ConditionModel[] } };
    return content.data ?? [];
  }

  /** Refreshes register terms without throwing; a late response cannot replace a newer load. */
  loadTermsAndConditions = async () => {
    const revision = this.#loadRevision;
    if (this.demoMode.isActive) {
      this.conditions = [];
      return;
    }

    const subdomain = this.subdomain || 'employee';

    try {
      const conditions = await this.#fetchTermsAndConditions(subdomain);
      if (revision === this.#loadRevision && subdomain === (this.subdomain || 'employee')) {
        this.conditions = conditions;
      }
    } catch (error) {
      if (revision !== this.#loadRevision) return;
      console.warn(
        `Failed to load preregister conditions from ${ENV.apiHost}/preregister/${subdomain}/conditions`,
        error
      );
      this.conditions = [];
    }
  };
}
